import { FormBuilder, FormGroup } from '@angular/forms';
import { LabelValue } from '@smartera/core';
import { getFormatedDate } from '@core/utils';
import { LabelMetaData } from '../models/label-meta-data';
import { FilterQuery, SearchFilter } from '../models/search-filter';
import { Key } from '../models/type';
import { AggregationField } from '../models/types';

export function getFilterFormGroup(fb: FormBuilder, field: string): FormGroup {
	const form = fb.group({
		field: [field],
		operator: ['match'],
		firstSearchPart: [null],
		lastSearchPart: [null],
	});

	form.get('lastSearchPart').disable();

	form.get('operator').valueChanges.subscribe((value) => {
		form.get('lastSearchPart').reset();
		form.get('firstSearchPart').reset();

		if (value === 'range' || value === 'dynamic') {
			form.get('lastSearchPart').enable();
		} else {
			form.get('lastSearchPart').disable();
		}
	});

	return form;
}

/*
	REFACTOR 1: 
		- İlk olarak elasticsearch query'si oluşturma işlemi düzenlenecek!
		- Gelen parametreler kontrol edilecek, parametre değelerine uygun type'lar verilecek!
		- Fonksiyon yeniden düzenlenecek.
 */
export function getSearchQuery(fieldsMetaData: any, fieldsKeys: string[], value: any): FilterQuery {
	const query = [];
	const fields = [];

	for (const key of fieldsKeys) {
		if (value[key]) {
			const filterData: SearchFilter = value[key];
			filterData.operator = filterData.operator || 'match';

			// when form reset the operator 👇 will be null 👀

			if (['match', 'term'].includes(filterData.operator) && filterData.firstSearchPart) {
				if (fieldsMetaData[key].type === 'date') {
					query.push({
						range: {
							[key]: {
								gte: getFormatedDate(filterData.firstSearchPart as Date),
								lte: getFormatedDate(filterData.firstSearchPart as Date),
								format: 'dd/MM/yyyy',
							},
						},
					});
				} else {
					if (filterData.operator === 'match') {
						const fsp = `*${filterData.firstSearchPart}*`;
						fields.push(key);
						query.push({
							query_string: {
								query: fsp,
								fields: [...fields],
								default_operator: 'AND',
							},
						});
					} else {
						const originalQueryStringArray: any = filterData.firstSearchPart;
						if (
							fieldsMetaData[key].type === 'double' ||
							fieldsMetaData[key].type === 'float' ||
							fieldsMetaData[key].type === 'long'
						) {
							query.push({
								[filterData.operator + 's']: {
									[`${key}`]: [originalQueryStringArray],
								},
							});
						} else {
							query.push({
								[filterData.operator + 's']: {
									[`${key}.keyword`]: originalQueryStringArray,
								},
							});
						}
					}
				}
			} else if (filterData.operator === 'range' || filterData.operator === 'dynamic') {
				if (fieldsMetaData[key].type === 'date') {
					query.push({
						range: {
							[key]: {
								gte:
									filterData.operator === 'dynamic'
										? filterData.firstSearchPart
										: getFormatedDate(filterData.firstSearchPart as Date),
								lte:
									filterData.operator === 'dynamic'
										? filterData.lastSearchPart
										: getFormatedDate(filterData.lastSearchPart as Date),
								format: 'dd/MM/yyyy',
							},
						},
					});
				} else {
					query.push({
						range: {
							[key]: {
								gte: filterData.firstSearchPart,
								lte: filterData.lastSearchPart,
							},
						},
					});
				}
			} else if (filterData.operator === 'indicator-date') {
				query.push({
					range: {
						[key]: {
							gte: `#DYNAMIC_DATE{${filterData.firstSearchPart}[format=dd/MM/yyyy]}`,
							lte: null,
							format: 'dd/MM/yyyy',
						},
					},
				});
			}
		}
	}

	return query;
}

/*
	AutoComplete için kullanılacak olan query'i hazırlayan method.
*/
export function getCompleteQuery(key: string, value: any): object[] {
	const query = [];
	if (value[key]) {
		const filterData: SearchFilter = value[key];
		const fsp = `*${filterData.firstSearchPart}*`;
		query.push({
			query_string: {
				query: fsp,
				fields: [key],
				default_operator: 'AND',
			},
		});
	}
	return query;
}

/*
	REFACTOR 2: Aggregation ElasticSearch Query İşlemi
		- Gelen parametreler kontrol edilecek, parametre değelerine uygun type'lar verilecek!
		- Fonksiyon yeniden düzenlenecek.
 */
export function getAggsQuery(
	columnsList: Key[],
	rowsList: Key[],
	dataCell: AggregationField[],
	configs: any,
	numberFieldslist: any[],
	labelMetaData: LabelMetaData
): any {
	const result = {};
	let current: any = null;
	current = result;

	/**
	 * Belirli bir sıra yok eklenen column ve row listelerini teker teker aggs bilgileriyle iç içe geçecek şekilde oluşturulur.
	 */
	for (const item of [...columnsList, ...rowsList]) {
		if (item.dateFormat) {
			// const dateFormat = setDdateFormat(item, labelMetaData);
			const missing = setMissingValue(item, labelMetaData, item.dateFormat);
			current[item.name] = {
				date_histogram: {
					field: `${item.name}`,
					// ...dateFormat,
					format: item.dateFormat.format,
					calendar_interval: item.dateFormat.interval,
					missing,
					min_doc_count: 1,
				},
			};
		} else if (numberFieldslist.includes(item.name)) {
			current[item.name] = {
				terms: {
					field: `${item.name}`,
					size: item.size ?? 100,
					missing: labelMetaData[item.name].nullValue,
				},
			};
		} else {
			current[item.name] = {
				// {BAYI:terms:{...}}
				terms: {
					field: `${item.name}.keyword`,
					size: item.size ?? 100,
					missing: labelMetaData[item.name].nullValue,
				},
			};
		}

		if (item.orderBy) {
			const order = {};
			order[item.orderBy] = item.order;
			if (item.dateFormat) {
				current[item.name].date_histogram['order'] = order;
			} else {
				current[item.name].terms['order'] = order;
			}
		}

		current[item.name].aggs = {};

		if (item.orderBy) {
			const aggFieldIndex = item.orderBy.slice(-1);
			const aggField = dataCell[aggFieldIndex];
			current[item.name].aggs[item.orderBy] = {};
			if (numberFieldslist.includes(aggField.field)) {
				current[item.name].aggs[item.orderBy][aggField.method] = {
					field: aggField.field,
				};
			} else {
				current[item.name].aggs[item.orderBy][aggField.method] = {
					field: `${aggField.field}.keyword`,
				};
			}
		}
		current = current[item.name].aggs;
		// current[k.name].aggs = {};  --> Same as above
		// current = current[k.name].aggs;
	}

	/**
	 * En son eklenen item'ın aggs bilgisine hücre'de seçilen methodları index sırasına göre ve method adına göre ekliyor.
	 */
	dataCell
		.filter(({ method, field }) => method && field)
		.forEach(({ method, field }, index) => {
			const id = getFieldMethodId(field, index);

			if (!current[id]) {
				current[id] = {};
			}

			if (method === 'cardinality' && !numberFieldslist.includes(field)) {
				current[id][method] = {
					field: field + '.keyword',
				};
			} else if (method === 'value_count') {
				// Adet gösteriminde null değerleri saymadığı için boş olan değerleri 0 olarak setleyerek getirecek.
				current[id][method] = {
					field: field,
					missing: 0,
				};
			} else {
				current[id][method] = {
					field: field,
				};
			}
		});

	return result;
}

/*
	REFACTOR 3: Other Aggregation ElasticSearch Query İşlemi
		- Buradaki işlemler Aggregation Query'si oluşturulan fonksiyon içerisinde yapılabilir mi kontrol edilecek.
		- Eğer yapılabiliyorsa ilgili fonksiyon içerisine taşınacak,
		- Eğer taşınmıyorsa bu method içerisinde refactor işlemleri yapılacak!
 */
export function getOtherAggsQuery(
	fields: Key[],
	dataCell: AggregationField[],
	numberFieldslist: string[],
	labelMetaData: LabelMetaData,
	withoutValues: string[]
) {
	const result = {};
	let current: any = null;
	current = result;
	for (const item of fields) {
		if (item.showOtherValues) {
			const terms = {};
			terms[item.name + '.keyword'] = withoutValues;
			current[item.name] = {
				filter: {
					bool: {
						must_not: [
							{
								terms: terms,
							},
						],
					},
				},
			};
		} else if (item.dateFormat) {
			const dateFormat = setDdateFormat(item, labelMetaData);
			const missing = setMissingValue(item, labelMetaData, dateFormat);
			current[item.name] = {
				date_histogram: {
					field: `${item.name}`,
					...dateFormat,
					missing,
					min_doc_count: 1,
				},
			};
		} else if (numberFieldslist.includes(item.name)) {
			current[item.name] = {
				terms: {
					field: `${item.name}`,
					size: item.size ?? 100,
					missing: labelMetaData[item.name].nullValue,
				},
			};
		} else {
			current[item.name] = {
				terms: {
					field: `${item.name}.keyword`,
					size: item.size ?? 100,
					missing: labelMetaData[item.name].nullValue,
				},
			};
		}

		if (!item.showOtherValues && item.orderBy) {
			const order = {};
			order[item.orderBy] = item.order;
			if (item.dateFormat) {
				current[item.name].date_histogram['order'] = order;
			} else {
				current[item.name].terms['order'] = order;
			}
		}

		current[item.name].aggs = {};

		if (!item.showOtherValues && item.orderBy) {
			const aggFieldIndex = item.orderBy.slice(-1);
			const aggField = dataCell[aggFieldIndex];
			current[item.name].aggs[item.orderBy] = {};
			if (numberFieldslist.includes(aggField.field)) {
				current[item.name].aggs[item.orderBy][aggField.method] = {
					field: aggField.field,
				};
			} else {
				current[item.name].aggs[item.orderBy][aggField.method] = {
					field: `${aggField.field}.keyword`,
				};
			}
		}

		current = current[item.name].aggs;
	}

	dataCell
		.filter(({ method, field }) => method && field)
		.forEach(({ method, field }, index) => {
			const id = getFieldMethodId(field, index);

			if (!current[id]) {
				current[id] = {};
			}

			if (method === 'cardinality' && !numberFieldslist.includes(field)) {
				current[id][method] = {
					field: field + '.keyword',
				};
			} else if (method === 'value_count') {
				// Adet gösteriminde null değerleri saymadığı için boş olan değerleri 0 olarak setleyerek getirecek.
				current[id][method] = {
					field: field,
					missing: 0,
				};
			} else {
				current[id][method] = {
					field: field,
				};
			}
		});

	return result;
}

enum DateFomat {
	day = 'dd/MM/yyyy',
	month = 'MM/yyyy',
	year = 'yyyy',
}

export function getFieldMethodId(fieldName: string, index: number): string {
	return `${fieldName}_${index}`;
}

export function dateParser(date: string, dateFormat: DateFomat): any {
	let nextData: Date;

	switch (dateFormat) {
		case DateFomat.day:
			// const [day, month, year] = date.split('/')
			// nextData = new Date(`${year}/${month}/${day}`);
			// nextData.setDate(nextData.getDate() + 1);
			return {
				gte: date,
				lte: date,
				format: DateFomat.day,
			};

		case DateFomat.month: {
			const [month, year] = date.split('/');

			nextData = new Date(`${year}/${month}/1`);
			nextData.setMonth(nextData.getMonth() + 1);
			return {
				gte: '01/' + date,
				lt: `${nextData.getDate() <= 9 ? '0' + nextData.getDate() : nextData.getDate()}/${
					nextData.getMonth() + 1 <= 9 ? '0' + (nextData.getMonth() + 1) : nextData.getMonth() + 1
				}/${nextData.getFullYear()}`,
				format: DateFomat.day,
			};
		}
		case DateFomat.year: {
			return {
				gte: date,
				lt: (+date + 1).toString(),
				format: DateFomat.year,
			};
		}
	}
}

export function getColumnsRowsKeyFromNameProp(args: any[]): string[] {
	return args.map((o) => o.name);
}

export function getValues(args: LabelValue<any, string>[]): string[] {
	return args.map((item) => item.value);
}

export function getKey(d: any = {}): string {
	return d.key_as_string || String(d.key);
}

// export function getSubLengthRowBase(
//   data,
//   columns: string[],
//   fieldsLength: number
// ): number {
//   const [column, ...nextColumns] = columns;
//   let result = 0;

//   if (columns.length == 1) {
//     result = data[column].buckets.length;
//   } else {
//     data[column].buckets.forEach((i) => {
//       result += getSubLengthRowBase(i, nextColumns, fieldsLength);
//     });
//   }

//   return result;
// }
export function getChildrenCount(data, columns: string[], fieldsLength: number = 1): number {
	const [column, ...nextColumns] = columns;
	let result = 0;

	if (columns.length == 1) {
		result = data[column].buckets.length * fieldsLength;
	} else {
		data[column].buckets.forEach((i) => {
			result += getChildrenCount(i, nextColumns, fieldsLength);
		});
	}

	return result;
}

export enum DatePriority {
	dayFirst = 'dd/MM/yyyy',
	monthFirst = 'MM/dd/yyyy',
	yearFirst = 'yyyy/MM/dd',
}

export enum EmptyDate {
	day = '01',
	month = '01',
	week = '01',
	year = '0001',
}

export const emptyDateString = ' ';

function setDdateFormat(key, lbm) {
	const {
		name,
		dateFormat: { interval },
	} = key;
	let format: string;
	switch (interval) {
		case 'year':
			format = 'yyyy';
			break;
		case 'month':
			switch (lbm[name]['format']) {
				case DatePriority.dayFirst:
					format = 'MM/yyy';
					break;
				case DatePriority.monthFirst:
					format = 'MM/yyy';
					break;
				case DatePriority.yearFirst:
					format = 'yyyy/MM';
			}
			break;
		case 'day':
			switch (lbm[name]['format']) {
				case DatePriority.dayFirst:
					format = 'dd/MM/yyy';
					break;
				case DatePriority.monthFirst:
					format = 'MM/dd/yyy';
					break;
				case DatePriority.yearFirst:
					format = 'yyyy/MM/dd';
			}
			break;

		case 'week':
			switch (lbm[name]['format']) {
				case DatePriority.dayFirst:
					format = 'ww/yyyy';
					break;
				case DatePriority.monthFirst:
					format = 'ww/yyy';
					break;
				case DatePriority.yearFirst:
					format = 'yyyy/ww';
					break;
			}
	}

	return { format, calendar_interval: interval };
}

//TODO: Refactor !!!
function setMissingValue(k, lbm, dateFormat) {
	let missing: string;
	const { name } = k;
	const { interval } = dateFormat;

	const number1 = makeNumberTwoDigit(lbm[name]['number1']);
	const number2 = makeNumberTwoDigit(lbm[name]['number2']);
	const number3 = makeNumberTwoDigit(lbm[name]['number3']);

	if (lbm[name]['isMissingValueEmpty']) {
		switch (lbm[name]['format']) {
			case DatePriority.dayFirst:
				switch (interval) {
					case 'year':
						missing = EmptyDate.year;
						return missing;
					case 'month':
						missing = `${EmptyDate.month}/${EmptyDate.year}`;
						return missing;
					case 'day':
						missing = `${EmptyDate.day}/${EmptyDate.month}/${EmptyDate.year}`;
						return missing;
					case 'week':
						missing = `${EmptyDate.week}/${EmptyDate.year}`;
						return missing;
				}
				break;
			case DatePriority.monthFirst:
				switch (interval) {
					case 'year':
						missing = EmptyDate.year;
						return missing;
					case 'month':
						missing = `${EmptyDate.month}/${EmptyDate.year}`;
						return missing;
					case 'day':
						missing = `${EmptyDate.day}/${EmptyDate.month}/${EmptyDate.year}`;
						return missing;
					case 'week':
						missing = `${EmptyDate.week}/${EmptyDate.year}`;
						return missing;
				}
				break;
			case DatePriority.yearFirst:
				switch (interval) {
					case 'year':
						missing = EmptyDate.year;
						return missing;
					case 'month':
						missing = `${EmptyDate.year}/${EmptyDate.month}`;
						return missing;
					case 'day':
						missing = `${EmptyDate.year}/${EmptyDate.month}/${EmptyDate.day}`;
						return missing;
					case 'week':
						missing = `${EmptyDate.year}/${EmptyDate.week}`;
						return missing;
				}
				break;
		}
	} else {
		switch (lbm[name]['format']) {
			case DatePriority.dayFirst:
				switch (interval) {
					case 'year':
						missing = number3;
						return missing;
					case 'month':
						missing = `${number2}/${number3}`;
						return missing;
					case 'day':
						missing = `${number1}/${number2}/${number3}`;
						return missing;
					case 'week':
						missing = `${number2}/${number3}`;
						return missing;
				}
				break;
			case DatePriority.monthFirst:
				switch (interval) {
					case 'year':
						missing = number3;
						return missing;
					case 'month':
						missing = `${number1}/${number3}`;
						return missing;
					case 'day':
						missing = `${number1}/${number2}/${number3}`;
						return missing;
					case 'week':
						missing = `${number2}/${number3}`;
						return missing;
				}
				break;
			case DatePriority.yearFirst:
				switch (interval) {
					case 'year':
						missing = number3;
						return missing;
					case 'month':
						missing = `${number1}/${number2}`;
						return missing;
					case 'day':
						missing = `${number1}/${number2}/${number3}`;
						return missing;
					case 'week':
						missing = `${number1}/${number3}`;
						return missing;
				}
				break;
		}
	}
}

function makeNumberTwoDigit(number: number): string {
	if (number < 10) {
		return '0' + number.toString();
	}
	return number.toString();
}
